import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AdminGate } from "@/components/AdminShell";
import {
  adminApi,
  type AdminOrgSummary,
  type CreateOrgResult,
  type ApiError,
} from "@/lib/api";
import { INDUSTRY_LABELS, KNOWN_VERTICALS } from "@/lib/verticalCopy";
import { INDUSTRY_MISSING, industryRefusal } from "@/lib/industryRefusal";

// ---------------------------------------------------------------------------
// Admin /tenants — create + list + basic-edit organizations.
// Sign-in gate + page chrome live in components/AdminShell.tsx (shared with
// /inquiries since R1). Access is gated server-side to the allowlist
// (patrick@/juan@); this page just surfaces the result.
// ---------------------------------------------------------------------------

const STATUSES = ["design_partner", "pilot", "active", "suspended", "churned"];

export default function Tenants() {
  return <AdminGate>{(idToken, signOut) => <TenantsAdmin idToken={idToken} onSignOut={signOut} />}</AdminGate>;
}

function TenantsAdmin({ idToken, onSignOut }: { idToken: string; onSignOut: () => void }) {
  const [orgs, setOrgs] = useState<AdminOrgSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateOrgResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { organizations } = await adminApi.listOrgs(idToken);
      setOrgs(organizations);
    } catch (err) {
      const e = err as ApiError;
      setError(e.status === 401 || e.status === 403 ? "Not authorized (sign in with an admin account)." : e.message);
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-slate-800">Organizations</h2>
        <Button variant="outline" size="sm" onClick={onSignOut}>Sign out</Button>
      </div>

      {created && <CreatedBanner result={created} onDismiss={() => setCreated(null)} />}

      <CreateOrgForm
        idToken={idToken}
        onCreated={(r) => {
          setCreated(r);
          void load();
        }}
      />

      <div className="mt-8">
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 mb-4">{error}</p>}
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <OrgTable orgs={orgs} idToken={idToken} onChanged={load} />
        )}
      </div>
    </div>
  );
}

export function CreatedBanner({ result, onDismiss }: { result: CreateOrgResult; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  const warnings = result.operator_warnings ?? [];
  return (
    <div className="mb-6 rounded-md border border-emerald-300 bg-emerald-50 p-4">
      <p className="text-sm font-semibold text-emerald-900">
        Created “{result.name}” ({result.scoring_rules_count} scoring rules, contact {result.primary_contact.email}).
      </p>
      {result.operator_emails.length > 0 && (
        <p className="mt-1 text-xs text-emerald-900">
          Operator access granted to: <strong>{result.operator_emails.join(", ")}</strong>. Each still needs a
          Supabase auth account to sign in.
        </p>
      )}
      {warnings.length > 0 && (
        <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="text-xs font-semibold text-amber-900">
            {warnings.length === 1 ? "One of those emails won’t take effect here:" : "Some of those emails won’t take effect here:"}
          </p>
          <ul className="mt-1 list-disc pl-4 text-xs text-amber-900">
            {warnings.map((w, i) => (
              <li key={`${w.email}-${w.shadowed_by?.org_id ?? i}`}>
                <strong>{w.email}</strong> already belongs to <strong>{w.shadowed_by?.org_name ?? "another org"}</strong> and
                will keep landing there — this new membership won’t change that.
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-amber-900">
            Use an email with no other org membership instead.
          </p>
        </div>
      )}
      <p className="mt-2 text-xs text-emerald-900">
        API key — copy it now, it can’t be retrieved later:
      </p>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-white px-2 py-1 text-xs border border-emerald-200">{result.api_key}</code>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            void navigator.clipboard.writeText(result.api_key);
            setCopied(true);
          }}
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      {result.subdomain && (
        <p className="mt-2 text-xs text-emerald-800">
          Reminder: set up DNS + TLS for <strong>{result.subdomain}.digavouch.com</strong>, and (in prod) add that
          origin to <code>ALLOWED_ORIGINS</code>.
        </p>
      )}
      <button className="mt-2 text-xs text-emerald-700 underline" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}

export function CreateOrgForm({ idToken, onCreated }: { idToken: string; onCreated: (r: CreateOrgResult) => void }) {
  const [name, setName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [status, setStatus] = useState("design_partner");
  // No default on purpose: the org's vertical decides what its operators see
  // (copy, and whether a rating is shown at all), so the admin must choose.
  const [industry, setIndustry] = useState("");
  const [industryErr, setIndustryErr] = useState<string | null>(null);
  const [emails, setEmails] = useState<string[]>(["juan@digavouch.com"]);
  const [operators, setOperators] = useState<string[]>([""]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const setEmail = (i: number, v: string) => setEmails((e) => e.map((x, j) => (j === i ? v : x)));
  const addEmail = () => setEmails((e) => [...e, ""]);
  const removeEmail = (i: number) => setEmails((e) => (e.length > 1 ? e.filter((_, j) => j !== i) : e));

  const setOperator = (i: number, v: string) => setOperators((e) => e.map((x, j) => (j === i ? v : x)));
  const addOperator = () => setOperators((e) => [...e, ""]);
  const removeOperator = (i: number) => setOperators((e) => (e.length > 1 ? e.filter((_, j) => j !== i) : [""]));

  const submit = async () => {
    setErr(null);
    setIndustryErr(null);
    const contactEmails = emails.map((e) => e.trim()).filter(Boolean);
    const operatorEmails = operators.map((e) => e.trim()).filter(Boolean);
    // Both checks run before either returns, so a form missing both says so
    // for both at once.
    const nameMissing = !name.trim();
    if (nameMissing) setErr("Name is required.");
    if (!industry) setIndustryErr(INDUSTRY_MISSING);
    if (nameMissing || !industry) return;
    setSubmitting(true);
    try {
      const result = await adminApi.createOrg(idToken, {
        name: name.trim(),
        subdomain: subdomain.trim() || null,
        contactEmails: contactEmails.length ? contactEmails : undefined,
        operatorEmails: operatorEmails.length ? operatorEmails : undefined,
        status,
        industry,
      });
      onCreated(result);
      setName("");
      setSubdomain("");
      setEmails(["juan@digavouch.com"]);
      setOperators([""]);
      setStatus("design_partner");
      setIndustry("");
    } catch (e) {
      const refusal = industryRefusal(e as ApiError);
      if (refusal) setIndustryErr(refusal);
      else setErr((e as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  };

  const input = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm";

  return (
    <div className="rounded-md border border-slate-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-slate-800 mb-4">Create organization</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="text-xs font-medium text-slate-600">
          Name
          <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Living" />
        </label>
        <label className="text-xs font-medium text-slate-600">
          Subdomain (optional)
          <input className={input} value={subdomain} onChange={(e) => setSubdomain(e.target.value)} placeholder="acme" />
        </label>
        <label className="text-xs font-medium text-slate-600">
          Status
          <select className={input} value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        {/* A div, not a wrapping label like its neighbours: the error line sits
            under the select, and inside a label it would join the field's name. */}
        <div className="text-xs font-medium text-slate-600">
          <label htmlFor="create-org-industry">Industry</label>
          <select
            id="create-org-industry"
            className={input}
            value={industry}
            required
            aria-invalid={industryErr ? true : undefined}
            aria-describedby={industryErr ? "create-org-industry-error" : undefined}
            onChange={(e) => {
              setIndustry(e.target.value);
              setIndustryErr(null);
            }}
          >
            <option value="" disabled>Choose an industry</option>
            {KNOWN_VERTICALS.map((v) => (
              <option key={v} value={v}>{INDUSTRY_LABELS[v]}</option>
            ))}
          </select>
          {industryErr && (
            <span id="create-org-industry-error" role="alert" className="mt-1 block text-xs text-red-700">
              {industryErr}
            </span>
          )}
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs font-medium text-slate-600 mb-1">Contact emails (first is primary; all get notifications)</p>
        {emails.map((email, i) => (
          <div key={i} className="mb-2 flex items-center gap-2">
            <input
              className={input}
              value={email}
              onChange={(e) => setEmail(i, e.target.value)}
              placeholder="name@example.com"
            />
            <Button variant="outline" size="sm" onClick={() => removeEmail(i)} disabled={emails.length === 1}>
              Remove
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addEmail}>+ Add another email</Button>
      </div>

      <div className="mt-4">
        <p className="text-xs font-medium text-slate-600 mb-1">
          Operator logins (optional — grants dashboard access for this org)
        </p>
        {operators.map((email, i) => (
          <div key={i} className="mb-2 flex items-center gap-2">
            <input
              className={input}
              value={email}
              onChange={(e) => setOperator(i, e.target.value)}
              placeholder="operator@example.com"
            />
            <Button variant="outline" size="sm" onClick={() => removeOperator(i)} disabled={operators.length === 1 && !operators[0]}>
              Remove
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addOperator}>+ Add another operator</Button>
        <p className="mt-1 text-[11px] text-slate-500">
          Each operator still needs a Supabase auth account (email/password) to sign in — create that
          separately. Adding it here grants the membership; it doesn’t create the login.
        </p>
      </div>

      {err && <p className="mt-3 text-sm text-red-700">{err}</p>}
      <div className="mt-4">
        <Button onClick={submit} disabled={submitting}>
          {submitting ? "Creating…" : "Create organization"}
        </Button>
      </div>
    </div>
  );
}

export function OrgTable({ orgs, idToken, onChanged }: { orgs: AdminOrgSummary[]; idToken: string; onChanged: () => void }) {
  if (orgs.length === 0) return <p className="text-sm text-slate-500">No organizations yet.</p>;
  return (
    <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="px-4 py-2">Name</th>
            <th className="px-4 py-2">Subdomain</th>
            <th className="px-4 py-2">Primary contact</th>
            <th className="px-4 py-2">Operators</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2">Keys</th>
          </tr>
        </thead>
        <tbody>
          {orgs.map((o) => (
            <OrgRow key={o.org_id} org={o} idToken={idToken} onChanged={onChanged} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OrgRow({ org, idToken, onChanged }: { org: AdminOrgSummary; idToken: string; onChanged: () => void }) {
  const [saving, setSaving] = useState(false);
  // Subdomain is editable in place. It used to be create-only: the PATCH
  // endpoint has always accepted it, but nothing in this table exposed it, so
  // an org provisioned without one could never get one. That is not a corner
  // case — the IDW demo org hit it (2026-08-25), and the only workarounds were
  // raw SQL on prod (skipping the format + duplicate validation this endpoint
  // exists to enforce) or hand-extracting a live admin bearer token.
  const [sub, setSub] = useState(org.subdomain ?? "");
  const [subError, setSubError] = useState<string | null>(null);
  const [subSaved, setSubSaved] = useState(false);

  const trimmedSub = sub.trim().toLowerCase();
  const currentSub = org.subdomain ?? "";
  const subDirty = trimmedSub !== currentSub;

  const changeStatus = async (status: string) => {
    setSaving(true);
    try {
      await adminApi.updateOrg(idToken, org.org_id, { status });
      onChanged();
    } finally {
      setSaving(false);
    }
  };

  const saveSubdomain = async () => {
    setSaving(true);
    setSubError(null);
    setSubSaved(false);
    try {
      // Empty clears it (the endpoint takes null). Anything else is sent
      // trimmed + lowercased, matching the server's own normalization so the
      // dirty check below doesn't flap on a value the server would rewrite.
      await adminApi.updateOrg(idToken, org.org_id, { subdomain: trimmedSub || null });
      setSubSaved(Boolean(trimmedSub));
      onChanged();
    } catch (e) {
      // Surfaced rather than swallowed: the two real failures here are a bad
      // format (400) and one already in use (409), and both are things the
      // operator has to see and act on. (The status dropdown above still
      // swallows its errors — separate fix, tracked.)
      setSubError((e as ApiError).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-t border-slate-100">
      <td className="px-4 py-2 font-medium text-slate-800">{org.name}</td>
      <td className="px-4 py-2 text-slate-600">
        <div className="flex items-center gap-1">
          <input
            className="w-28 rounded-md border border-slate-300 px-2 py-1 text-xs"
            value={sub}
            placeholder="—"
            aria-label={`Subdomain for ${org.name}`}
            disabled={saving}
            onChange={(e) => { setSub(e.target.value); setSubError(null); setSubSaved(false); }}
          />
          {subDirty && (
            <button
              type="button"
              className="rounded-md bg-slate-800 px-2 py-1 text-xs text-white disabled:opacity-50"
              disabled={saving}
              onClick={saveSubdomain}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          )}
        </div>
        {subError && <p className="mt-1 text-xs text-red-700">{subError}</p>}
        {subSaved && (
          <p className="mt-1 text-xs text-emerald-800">
            Set up DNS + TLS for <strong>{trimmedSub}.digavouch.com</strong>, and (in prod) add that origin to{" "}
            <code>ALLOWED_ORIGINS</code>.
          </p>
        )}
      </td>
      <td className="px-4 py-2 text-slate-600">{org.primary_contact?.email ?? "—"}</td>
      <td className="px-4 py-2 text-xs text-slate-500">{(org.operator_emails ?? []).join(", ") || "—"}</td>
      <td className="px-4 py-2">
        <select
          className="rounded-md border border-slate-300 px-2 py-1 text-xs"
          value={org.status}
          disabled={saving}
          onChange={(e) => changeStatus(e.target.value)}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-2 text-xs text-slate-500">{(org.active_key_prefixes ?? []).join(", ") || "—"}</td>
    </tr>
  );
}
